/**
 * FinancialsTab — Top-level financials page tabs orchestrator.
 *
 * Feb 2026 refactor (iter138.9al):
 *  - Was a 2094-line god-component owning every tab's state + JSX.
 *  - Now a slim orchestrator that wires up tab triggers + delegates each
 *    tab's UI + state to a dedicated component in ./tabs/.
 *
 * Tabs:
 *  - Overview        → ExecutiveSummaryTab
 *  - Mechanic Productivity → MechanicProductivityTab (NEW)
 *  - Forecast        → CashFlowForecastTab
 *  - Profit & Loss   → PLAnalysisTab (NEW)
 *  - Cash Incentives → CashIncentivesTab
 *  - GST Board       → GstBoardTab
 *  - Daily Entries   → DailyEntriesTab (NEW)
 */
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Activity, Users, Zap, TrendingUp, DollarSign, Receipt,
} from 'lucide-react';
import CashIncentivesTab from './CashIncentivesTab';
import GstBoardTab from './GstBoardTab';
import ExecutiveSummaryTab from './ExecutiveSummaryTab';
import CashFlowForecastTab from './CashFlowForecastTab';
import PLAnalysisTab from './tabs/PLAnalysisTab';
import DailyEntriesTab from './tabs/DailyEntriesTab';
import MechanicProductivityTab from './tabs/MechanicProductivityTab';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FinancialsTab({ currentUser }) {
  const userRole = currentUser?.role;
  const isReadOnly = userRole === 'service_manager' || userRole === 'floor_manager';
  const { isDisabled } = useFeatureFlags();

  return (
    <div className="space-y-6" data-testid="financials-tab">
      <Tabs defaultValue={isReadOnly ? 'productivity' : 'overview'} className="w-full">
        <TabsList
          className={`grid w-full ${isReadOnly ? 'max-w-md grid-cols-1' : 'max-w-6xl grid-cols-7'}`}
          data-testid="financials-main-tabs"
        >
          {!isReadOnly && (
            <TabsTrigger value="overview" data-testid="tab-overview">
              <Activity className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
          )}
          <TabsTrigger value="productivity" data-testid="tab-productivity">
            <Users className="h-4 w-4 mr-2" />
            Mechanic Productivity
          </TabsTrigger>
          {!isReadOnly && (
            <>
              {!isDisabled('financials_forecast') && (
                <TabsTrigger value="forecast" data-testid="tab-forecast">
                  <Zap className="h-4 w-4 mr-2" />
                  Forecast
                </TabsTrigger>
              )}
              {!isDisabled('financials_pnl') && (
                <TabsTrigger value="pl" data-testid="tab-pl">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Profit & Loss
                </TabsTrigger>
              )}
              {!isDisabled('financials_cash_incentives') && (
                <TabsTrigger value="cash" data-testid="tab-cash">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Cash Incentives
                </TabsTrigger>
              )}
              <TabsTrigger value="gst" data-testid="tab-gst">
                <Receipt className="h-4 w-4 mr-2" />
                GST Board
              </TabsTrigger>
              {!isDisabled('financials_daily_entries') && (
                <TabsTrigger value="entries" data-testid="tab-entries">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Daily Entries
                </TabsTrigger>
              )}
            </>
          )}
        </TabsList>

        {!isReadOnly && (
          <TabsContent value="overview" className="space-y-6 mt-6">
            <ExecutiveSummaryTab BACKEND_URL={BACKEND_URL} />
          </TabsContent>
        )}

        {!isReadOnly && (
          <TabsContent value="forecast" className="space-y-6 mt-6">
            <CashFlowForecastTab BACKEND_URL={BACKEND_URL} />
          </TabsContent>
        )}

        <TabsContent value="productivity" className="space-y-4 mt-6">
          <MechanicProductivityTab isReadOnly={isReadOnly} />
        </TabsContent>

        <TabsContent value="pl" className="space-y-6 mt-6">
          <PLAnalysisTab />
        </TabsContent>

        <TabsContent value="cash" className="space-y-6 mt-6">
          <CashIncentivesTab BACKEND_URL={BACKEND_URL} />
        </TabsContent>

        <TabsContent value="gst" className="space-y-6 mt-6">
          <GstBoardTab BACKEND_URL={BACKEND_URL} />
        </TabsContent>

        <TabsContent value="entries" className="space-y-6 mt-6">
          <DailyEntriesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
